import { getCampaignRunsQueryOptions } from "@/api/campaign.api";
import { createLeadsQueryOptions } from "@/api/leads.api";
import { createGhostItems, ghostItem } from "@/components/generic/Table";
import {
  getPushMessageLeads,
  usePushMessageLeads,
} from "@/services/push-message-leads";
import { Lead } from "@/types/campaign";
import { PagedResult, queryClient } from "@/utils/api";
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

function getConditional<Condition extends boolean, T, U>(
  condition: Condition,
  trueValue: T,
  falseValue: U,
) {
  return (condition ? trueValue : falseValue) as Condition extends true ? T : U;
}

type BooleanDefault<
  T extends boolean | undefined,
  Default extends boolean,
> = T extends boolean ? T : Default;

function booleanDefault<T extends boolean | undefined, Default extends boolean>(
  value: T,
  defaultValue: Default,
): BooleanDefault<T, Default> {
  return (value ?? defaultValue) as BooleanDefault<T, Default>;
}

function combineLeads({
  queryLeads,
  pushMessageLeads,
}: {
  queryLeads: Lead[];
  pushMessageLeads: Lead[];
}) {
  const map = new Map<number, Lead>();

  for (const lead of pushMessageLeads) {
    map.set(lead.id, lead);
  }

  for (const lead of queryLeads) {
    map.set(lead.id, lead);
  }

  return [...map.values()];
}

export async function getLeads({
  params,
}: {
  params: Parameters<typeof createLeadsQueryOptions>[0];
}) {
  const queryOptions = createLeadsQueryOptions(params);
  const queryLeads = (
    await queryClient.ensureInfiniteQueryData(queryOptions)
  ).pages.flatMap((page) => page.results);
  const pushMessageLeads = getPushMessageLeads(queryOptions.queryKey);
  return combineLeads({ queryLeads, pushMessageLeads });
}

export function useLeads<Ghosts extends boolean | undefined = false>({
  enabled = true,
  campaignId,
  params,
  ghosts: _ghosts = false,
  onFirstLeadReceived,
}: {
  enabled?: boolean;
  campaignId?: number;
  params?: Parameters<typeof createLeadsQueryOptions>[0];
  ghosts?: Ghosts;
  onFirstLeadReceived?: () => void;
}) {
  const ghosts = booleanDefault(_ghosts as Ghosts, false);
  const firstLeadReceived = useRef(false);

  const { data: campaignRuns } = useQuery({
    ...getCampaignRunsQueryOptions(campaignId ?? NaN),
    enabled: enabled && campaignId !== undefined,
  });

  const activeRun = useMemo(
    () =>
      campaignRuns?.latestRun && campaignRuns.latestRun.completed === null
        ? {
            ...campaignRuns.latestRun,
            isFirstRun: campaignRuns.totalRuns === 1,
          }
        : null,
    [campaignRuns],
  );

  const leadsQuery = useLeadsQuery(
    useMemo(
      () =>
        params
          ? {
              queryOptions: {
                ...createLeadsQueryOptions(params),
                enabled,
              },
              params,
            }
          : {
              queryOptions: infiniteQueryOptions({
                queryKey: ["leads", {}] as const,
                queryFn: (): PagedResult<Lead> => {
                  console.error("No query");
                  throw new Error("No query");
                },
                enabled: false,
                initialPageParam: 1,
                getNextPageParam: () => undefined,
              }),
              params: {},
            },
      [enabled, params],
    ),
  );

  const queryLeads = useMemo(
    () => leadsQuery.data?.pages.flatMap((page) => page.results) ?? [],
    [leadsQuery.data?.pages],
  );

  const pushMessageLeads = usePushMessageLeads(leadsQuery.latestDataQueryKey);

  useEffect(() => {
    // Check if this is the first lead received
    if (
      queryLeads.length === 0 &&
      pushMessageLeads.length >= 1 &&
      !firstLeadReceived.current
    ) {
      console.debug("First lead received - calling onFirstLeadReceived");
      firstLeadReceived.current = true;
      onFirstLeadReceived?.();
    }
  }, [pushMessageLeads, queryLeads, onFirstLeadReceived]);

  const { leads, leadsWithGhosts } = useMemo(() => {
    const leads: Lead[] = [];
    const leadsWithGhosts: (Lead | typeof ghostItem)[] = [];

    if (leadsQuery.isPending) return { leads, leadsWithGhosts };

    leads.push(...combineLeads({ queryLeads, pushMessageLeads }));
    leadsWithGhosts.push(...leads);

    if (ghosts && activeRun) {
      const remainingRunLeads = Math.max(
        activeRun.requestedLeads - (activeRun.delivered ?? 0),
        0,
      );

      // Add ghost items if there are remaining leads in the run and no name filter is applied
      if (!leadsQuery.latestDataParams.name && remainingRunLeads) {
        if (queryLeads.length) {
          // Add a single ghost item to the beginning if there are already leads present
          leadsWithGhosts.unshift(ghostItem);
        } else {
          // Add ghost items to represent all remaining leads if no leads are present yet
          leadsWithGhosts.push(
            ...createGhostItems(Math.min(remainingRunLeads, 20)),
          );
        }
      }
    }

    return { leads, leadsWithGhosts };
  }, [
    leadsQuery.isPending,
    activeRun,
    queryLeads,
    pushMessageLeads,
    leadsQuery.latestDataParams.name,
    ghosts,
  ]);

  return {
    leadsQuery,
    queryLeads,
    pushMessageLeads,
    leads: getConditional(ghosts, leadsWithGhosts, leads),
  };
}

function useLeadsQuery({
  queryOptions,
  params,
}: {
  queryOptions: ReturnType<typeof createLeadsQueryOptions>;
  params: Parameters<typeof createLeadsQueryOptions>[0];
}) {
  const query = useInfiniteQuery(queryOptions);

  const currentQueryKey = queryOptions.queryKey;
  const currentParams = params;

  const [latestDataQueryKey, setLatestDataQueryKey] = useState(currentQueryKey);

  const [latestDataParams, setLatestDataParams] = useState(currentParams);

  useEffect(() => {
    if (query.data?.pages.length && !query.isPlaceholderData) {
      setLatestDataQueryKey(currentQueryKey);
      setLatestDataParams(currentParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data?.pages, query.isPlaceholderData]);

  return {
    ...query,
    currentQueryKey,
    currentParams,
    latestDataQueryKey,
    latestDataParams,
  };
}
